import { Text, ButtonV2 } from "@asayinc/component-library";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LoadingCard } from "src/components/molecules/Loading";
import {
  useShareRangeAnalysisEventQuery,
  useInstitutionNotificationsQuery,
} from "src/store/shareRangeAnalysisEvent";
import { SendShareRangeAnalysisReportDialog } from "../SendShareRangeAnalysisReportDialog";
import { useCurrentUserQuery } from "src/store/user";

export function ShareRangeAnalysisEventReportsTab() {
  const { id } = useParams() as { id: string };
  const {
    data: shareRangeAnalysisEvent,
    isLoading,
    error,
  } = useShareRangeAnalysisEventQuery({ id });
  const { data: institutionNotifications, isLoading: isLoadingNotifications } =
    useInstitutionNotificationsQuery({
      eventId: id,
      type: "intermediary_portal_email_share_range_analysis_report",
    });
  const { data: currentUser } = useCurrentUserQuery();
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  const isAdmin =
    currentUser?.isIntermediaryPortalAdmin ||
    currentUser?.isIntermediaryPortalSuperAdmin;

  if (error || (!isLoading && !shareRangeAnalysisEvent)) {
    return <ErrorState />;
  }

  if (isLoading || isLoadingNotifications) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <LoadingCard />
      </ScrollContainer>
    );
  }

  const { scheduledGenerationAt, generatedAt } = shareRangeAnalysisEvent;

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <SendShareRangeAnalysisReportDialog
        isOpen={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        shareRangeAnalysisEventId={id}
      />
      <Card title="Initial Report Generation">
        <Stack sx={{ p: 4 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "300px 250px 1fr",
              gap: 2,
              py: 3,
              px: 2,
              borderBottom: "1px solid #e0e0e0",
              alignItems: "center",
            }}
          >
            <Text variant="body3" sx={{ fontWeight: 600 }}>
              Type
            </Text>
            <Text variant="body3" sx={{ fontWeight: 600 }}>
              Scheduled Generation
            </Text>
            <Text variant="body3" sx={{ fontWeight: 600 }}>
              Generated
            </Text>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "300px 250px 1fr",
              gap: 2,
              py: 3,
              px: 2,
              alignItems: "center",
            }}
          >
            <Text variant="body2">Share Range Analysis Report</Text>
            <Text variant="body2">
              {scheduledGenerationAt
                ? dayjs(scheduledGenerationAt).format("MM/DD/YYYY HH:mm")
                : "-"}
            </Text>
            <Stack direction="row" spacing={2} alignItems="center">
              <Text variant="body2" sx={{ minWidth: "200px" }}>
                {generatedAt
                  ? dayjs(generatedAt).format("MM/DD/YYYY HH:mm")
                  : "-"}
              </Text>
              {generatedAt && isAdmin && (
                <ButtonV2
                  size="small"
                  variant="secondary"
                  onClick={() => setSendDialogOpen(true)}
                >
                  Send Again
                </ButtonV2>
              )}
            </Stack>
          </Box>
        </Stack>
      </Card>

      {institutionNotifications && institutionNotifications.length > 0 && (
        <Card title="Additional Report Sends" sx={{ mt: 8 }}>
          <Stack sx={{ p: 4 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "300px 250px 1fr",
                gap: 2,
                py: 3,
                px: 2,
                borderBottom: "1px solid #e0e0e0",
                alignItems: "center",
              }}
            >
              <Text variant="body3" sx={{ fontWeight: 600 }}>
                To
              </Text>
              <Text variant="body3" sx={{ fontWeight: 600 }}>
                Sent At
              </Text>
              <Text variant="body3" sx={{ fontWeight: 600 }}>
                Status
              </Text>
            </Box>
            {institutionNotifications.map((notification) => (
              <Box
                key={notification.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "300px 250px 1fr",
                  gap: 2,
                  py: 3,
                  px: 2,
                  borderBottom: "1px solid #f5f5f5",
                  alignItems: "center",
                }}
              >
                <Text variant="body2">
                  {notification.toEmailAddresses.join(", ")}
                </Text>
                <Text variant="body2">
                  {notification.sentAt
                    ? dayjs(notification.sentAt).format("MM/DD/YYYY HH:mm")
                    : "-"}
                </Text>
                <Box>
                  {notification.sentAt ? (
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 2,
                        py: 1,
                        borderRadius: "4px",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                      }}
                    >
                      <Text variant="body3">Sent</Text>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 2,
                        py: 1,
                        borderRadius: "4px",
                        backgroundColor: "#FFF3E0",
                        color: "#E65100",
                      }}
                    >
                      <Text variant="body3">Pending</Text>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Card>
      )}
    </ScrollContainer>
  );
}
